import { yupResolver } from "@hookform/resolvers/yup";
import { Eye, EyeOff } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@krea/shared/design-system/Form";
import { Input } from "@krea/shared/design-system/inputs/Input";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";
import { useUpdateLenderCompanyMutation } from "../hooks/use-update-lender-company.mutation";

const schema = yup.object().shape({
  webhookLenderOauthAuthUrl: yup.string().url("Invalid URL").nonNullable(),
  webhookLenderOauthClientId: yup.string().nonNullable(),
  integrationNotificationApiUrl: yup.string().url("Invalid URL").nonNullable(),
  webhookLenderOauthClientSecret: yup.string().nonNullable(),
  webhookLenderOauthScope: yup.string().nonNullable(),
  webhookLenderTokenType: yup.string().nonNullable(),
  webhookLenderApiKey: yup.string().nonNullable(),
});

type IWebhookConfigSectionFormValues = yup.InferType<typeof schema>;

export const WebhookConfigSection = () => {
  const [showOAuthSecret, setShowOAuthSecret] = useState<boolean>(false);
  const [showApiKey, setShowApiKey] = useState<boolean>(false);

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutate: updateLenderCompany } = useUpdateLenderCompanyMutation();

  const form = useForm<IWebhookConfigSectionFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      webhookLenderOauthAuthUrl: "",
      webhookLenderOauthClientId: "",
      webhookLenderOauthClientSecret: "",
      webhookLenderOauthScope: "",
      integrationNotificationApiUrl: "",
      webhookLenderTokenType: "",
      webhookLenderApiKey: "",
    },
  });
  useEffect(() => {
    const initialValues: IWebhookConfigSectionFormValues = {};

    if (lenderCompany?.integrationNotificationApiUrl) {
      initialValues.integrationNotificationApiUrl =
        lenderCompany.integrationNotificationApiUrl;
    }

    if (lenderCompany?.webhookLenderOauthAuthUrl) {
      initialValues.webhookLenderOauthAuthUrl =
        lenderCompany.webhookLenderOauthAuthUrl;
    }

    if (lenderCompany?.webhookLenderOauthClientId) {
      initialValues.webhookLenderOauthClientId =
        lenderCompany.webhookLenderOauthClientId;
    }

    if (lenderCompany?.webhookLenderApiKey) {
      initialValues.webhookLenderApiKey = lenderCompany.webhookLenderApiKey;
    }

    if (lenderCompany?.webhookLenderOauthScope) {
      initialValues.webhookLenderOauthScope =
        lenderCompany.webhookLenderOauthScope;
    }

    if (lenderCompany?.webhookLenderTokenType) {
      initialValues.webhookLenderTokenType =
        lenderCompany.webhookLenderTokenType;
    }

    if (lenderCompany?.webhookLenderOauthClientSecret) {
      initialValues.webhookLenderOauthClientSecret =
        lenderCompany.webhookLenderOauthClientSecret;
    }

    if (lenderCompany) {
      form.reset(initialValues);
    }
  }, [lenderCompany, form]);

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;

    form.handleSubmit((data) => {
      updateLenderCompany(
        { lenderCompanyId: lenderCompany.id, data },
        {
          onSuccess: () => {
            toast.success("Webhook configuration updated successfully");
          },
          onError: () => {
            toast.error("Failed to update webhook configuration");
          },
        },
      );
    })();
  }, [form, updateLenderCompany, lenderCompany]);

  return (
    <Card>
      <Form {...form}>
        <CardContent className="space-y-6 pb-8">
          <div className="">
            <h1 className="text-xl font-bold">Webhook Configuration</h1>
            <p className="text-sm text-black-100">
              Configure webhooks to receive real-time notifications about events
              in your account.
            </p>
          </div>
          <div>
            <h3 className="mb-2 text-base font-semibold">
              Webhook Notification API URL
            </h3>
          </div>
          <FormField
            control={form.control}
            name="integrationNotificationApiUrl"
            render={({ field, formState: { isSubmitting } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="url"
                    placeholder="https://your-domain.com/api/webhook"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormDescription>
                  The endpoint where webhook notifications will be sent.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <Separator />
          <div>
            <h3 className="mb-2 text-base font-semibold">
              OAuth Authentication (Optional)
            </h3>
            <p className="mb-6 text-sm">
              Configure OAuth authentication for secure webhook delivery. If you
              don&apos;t want to use OAuth you can leave these fields blank.
            </p>

            <div className="space-y-5">
              <FormField
                control={form.control}
                name="webhookLenderOauthAuthUrl"
                render={({ field, formState: { isSubmitting } }) => (
                  <FormItem>
                    <FormLabel>OAuth Authorization URL</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://auth.your-domain.com/oauth/authorize"
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormDescription>
                      The URL to authorize the webhook.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="webhookLenderOauthClientId"
                  render={({ field, formState: { isSubmitting } }) => (
                    <FormItem>
                      <FormLabel>OAuth Client ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="your-oauth-client-id"
                          autoComplete="off"
                          {...field}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        The ID of the OAuth client.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="webhookLenderOauthClientSecret"
                  render={({ field, formState: { isSubmitting } }) => (
                    <FormItem>
                      <FormLabel>OAuth Client Secret</FormLabel>
                      <FormControl>
                        <Input
                          type={showOAuthSecret ? "text" : "password"}
                          placeholder="••••••••••••••••••••••••••••••••"
                          {...field}
                          autoComplete="off"
                          disabled={isSubmitting}
                          rightElement={
                            <button
                              type="button"
                              onClick={() => setShowOAuthSecret((v) => !v)}
                              className="pointer-events-auto cursor-pointer"
                              aria-label="Toggle OAuth secret visibility"
                            >
                              {showOAuthSecret ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        The secret of the OAuth client.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="webhookLenderOauthScope"
                render={({ field, formState: { isSubmitting } }) => (
                  <FormItem>
                    <FormLabel>OAuth Scope</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="read write webhooks"
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormDescription>
                      Space-separated list of OAuth scopes required for webhook
                      access.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="mb-2 text-base font-semibold">
              Api Key Authentication (Optional)
            </h3>
            <p className="mb-6 text-sm">
              If not using OAuth, you can authenticate webhooks using an API
              key.
            </p>
          </div>

          <FormField
            control={form.control}
            name="webhookLenderApiKey"
            render={({ field, formState: { isSubmitting } }) => (
              <FormItem>
                <FormLabel>Webhook API Key</FormLabel>
                <FormControl>
                  <Input
                    type={showApiKey ? "text" : "password"}
                    placeholder="••••••••••••••••••••••••••••••••"
                    {...field}
                    disabled={isSubmitting}
                    rightElement={
                      <button
                        type="button"
                        onClick={() => setShowApiKey((v) => !v)}
                        className="pointer-events-auto cursor-pointer"
                        aria-label="Toggle API key visibility"
                      >
                        {showApiKey ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    }
                  />
                </FormControl>
                <FormDescription>
                  The API key to use for authentication. This key will be sent
                  in the Authorization header of webhook requests.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="webhookLenderTokenType"
            render={({ field, formState: { isSubmitting } }) => (
              <FormItem>
                <FormLabel>Token Type</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Bearer"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormDescription>
                  The type of token to use for authentication.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <Separator />
          <div className="flex justify-end">
            <Button
              type="button"
              size="lg"
              onClick={handleSubmit}
              disabled={
                form.formState.isSubmitting ||
                !form.formState.isValid ||
                !form.formState.isDirty
              }
            >
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Form>
    </Card>
  );
};
